// src/hooks/useTasteCalibrationStatus.ts
//
// Reads /users/{uid}.tasteCalibration to determine whether we
// should show the cold-start calibration overlay. Stays out of
// authStore so the auth flow doesn't depend on this read — it's a
// secondary concern that only Discover cares about.
//
// Returns: { needed, loading }
//   needed  — true if user is signed in and has no calibration record
//   loading — true while we haven't resolved yet (don't show overlay
//             during this state to avoid a flash)

import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../services/firebase';

interface CalibrationStatus {
  needed: boolean;
  loading: boolean;
  /** Imperative dismiss — call this after the user completes or
   *  skips the overlay so we don't re-prompt during this session
   *  even before the server-side write propagates. */
  dismiss: () => void;
}

export function useTasteCalibrationStatus(): CalibrationStatus {
  const [needed, setNeeded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (cancelled) return;
      if (!user || user.isAnonymous) {
        // Anonymous users don't get calibration — there's no
        // persistent profile to write to and we don't want to add
        // a friction moment during the anon-experience.
        setNeeded(false);
        setLoading(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (cancelled) return;
        if (!snap.exists()) {
          // Profile doc is created on consent or signup — if it
          // doesn't exist yet, calibration will be checked again on
          // the next render once it appears.
          setNeeded(false);
          setLoading(false);
          return;
        }
        const data = snap.data() as any;
        const cal = data?.tasteCalibration;
        // "Done" means either completed or explicitly skipped.
        // Both record completedAt; we only re-prompt if the field
        // is missing entirely.
        const done = !!cal?.completedAt;
        setNeeded(!done);
      } catch (err) {
        console.warn('useTasteCalibrationStatus failed:', err);
        // On error, default to NOT showing calibration. Better to
        // miss a one-time prompt than to surprise existing users
        // with a re-prompt because their profile read flapped.
        setNeeded(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const dismiss = () => setNeeded(false);

  return { needed, loading, dismiss };
}
